import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Save, RotateCcw, Plus, Trash2, GripVertical, ChevronDown, ChevronUp, Settings2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TemplateEditor = () => {
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedClauses, setExpandedClauses] = useState({});
  const [showCompanyInfo, setShowCompanyInfo] = useState(false);

  const fetchTemplate = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/templates/unified`);
      setTemplate(res.data);
    } catch {
      toast.error("Failed to load template");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTemplate(); }, [fetchTemplate]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/templates/unified`, template);
      toast.success("Template saved successfully");
    } catch {
      toast.error("Failed to save template");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm("Reset this template to default values? All your edits will be lost.")) return;
    try {
      const res = await axios.post(`${API}/templates/reset/unified`);
      setTemplate(res.data);
      toast.success("Template reset to defaults");
    } catch {
      toast.error("Failed to reset template");
    }
  };

  const updateCompanyInfo = (field, value) => {
    setTemplate(prev => ({
      ...prev,
      company_info: { ...prev.company_info, [field]: value }
    }));
  };

  const updateClause = (index, field, value) => {
    setTemplate(prev => {
      const clauses = [...prev.clauses];
      clauses[index] = { ...clauses[index], [field]: value };
      return { ...prev, clauses };
    });
  };

  const addClause = () => {
    setTemplate(prev => ({
      ...prev,
      clauses: [...prev.clauses, { number: String(prev.clauses.length + 1), title: "NEW CLAUSE", text: "" }]
    }));
    setExpandedClauses(prev => ({ ...prev, [template.clauses.length]: true }));
  };

  const removeClause = (index) => {
    if (!window.confirm("Remove this clause?")) return;
    setTemplate(prev => ({
      ...prev,
      clauses: prev.clauses.filter((_, i) => i !== index).map((c, i) => ({ ...c, number: String(i + 1) }))
    }));
  };

  const moveClause = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= template.clauses.length) return;
    setTemplate(prev => {
      const clauses = [...prev.clauses];
      [clauses[index], clauses[newIndex]] = [clauses[newIndex], clauses[index]];
      return { ...prev, clauses: clauses.map((c, i) => ({ ...c, number: String(i + 1) })) };
    });
  };

  const toggleClause = (index) => {
    setExpandedClauses(prev => ({ ...prev, [index]: !prev[index] }));
  };

  const updateAnnexure = (index, field, value) => {
    setTemplate(prev => {
      const annexures = [...prev.annexures];
      annexures[index] = { ...annexures[index], [field]: value };
      return { ...prev, annexures };
    });
  };

  const addAnnexure = () => {
    setTemplate(prev => ({
      ...prev,
      annexures: [...prev.annexures, { id: String(prev.annexures.length + 1), description: "", remarks: "" }]
    }));
  };

  const removeAnnexure = (index) => {
    setTemplate(prev => ({
      ...prev,
      annexures: prev.annexures.filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--navy)' }} />
      </div>
    );
  }

  if (!template) return null;

  return (
    <div className="animate-fadeIn" data-testid="template-editor-page">
      <div className="breadcrumb">
        <Link to="/">Dashboard</Link>
        <span>&rsaquo;</span>
        <span>Template Editor</span>
      </div>

      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 className="page-title">Master Template Editor</h1>
          <p className="page-subtitle">Single unified template for all Work Orders, AMCs, and Service Contracts</p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button variant="outline" onClick={handleReset} data-testid="reset-template-btn"
            style={{ borderColor: 'var(--red)', color: 'var(--red)' }}>
            <RotateCcw className="w-4 h-4 mr-2" /> Reset to Default
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-template-btn"
            style={{ background: 'var(--navy)', color: '#fff' }}>
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save Template
          </Button>
        </div>
      </div>

      {/* Company Info Section */}
      <div className="form-card" style={{ marginBottom: '20px' }}>
        <div 
          className="form-section-header" 
          onClick={() => setShowCompanyInfo(!showCompanyInfo)}
          style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
          data-testid="company-info-toggle"
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Settings2 className="w-5 h-5" style={{ color: 'var(--navy)' }} />
            <div>
              <div className="form-section-title" style={{ margin: 0 }}>Company Information (First Party)</div>
              <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Gnosis Hospitality LLP details used in all contracts</div>
            </div>
          </div>
          {showCompanyInfo ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </div>
        {showCompanyInfo && (
          <div className="form-body-inner" style={{ padding: '20px' }}>
            <div className="form-grid-2">
              <div>
                <Label>Company Name</Label>
                <Input value={template.company_info?.name || ''} 
                  onChange={e => updateCompanyInfo('name', e.target.value)}
                  data-testid="company-name-input" />
              </div>
              <div>
                <Label>Entity Type</Label>
                <Input value={template.company_info?.type || ''} 
                  onChange={e => updateCompanyInfo('type', e.target.value)} />
              </div>
            </div>
            <div style={{ marginTop: '12px' }}>
              <Label>Registered Office</Label>
              <Textarea value={template.company_info?.registered_office || ''} 
                onChange={e => updateCompanyInfo('registered_office', e.target.value)} rows={2} />
            </div>
            <div style={{ marginTop: '12px' }}>
              <Label>Additional Place of Business</Label>
              <Textarea value={template.company_info?.additional_office || ''} 
                onChange={e => updateCompanyInfo('additional_office', e.target.value)} rows={2} />
            </div>
            <div className="form-grid-2" style={{ marginTop: '12px' }}>
              <div>
                <Label>Representative Name</Label>
                <Input value={template.company_info?.representative_name || ''} 
                  onChange={e => updateCompanyInfo('representative_name', e.target.value)} />
              </div>
              <div>
                <Label>Designation</Label>
                <Input value={template.company_info?.representative_designation || ''} 
                  onChange={e => updateCompanyInfo('representative_designation', e.target.value)} />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Template Header */}
      <div className="form-card" style={{ marginBottom: '20px' }}>
        <div className="form-body-inner" style={{ padding: '20px' }}>
          <div className="form-grid-2">
            <div>
              <Label>Document Title</Label>
              <Input value={template.title || ''} 
                onChange={e => setTemplate(prev => ({ ...prev, title: e.target.value }))}
                data-testid="template-title-input" />
            </div>
            <div>
              <Label>Subtitle</Label>
              <Input value={template.subtitle || ''} 
                onChange={e => setTemplate(prev => ({ ...prev, subtitle: e.target.value }))} />
            </div>
          </div>
        </div>
      </div>

      {/* Section B Header */}
      <div className="form-card" style={{ marginBottom: '20px' }}>
        <div className="form-body-inner" style={{ padding: '20px' }}>
          <Label>General Terms Section Title</Label>
          <Input value={template.section_b_title || ''} 
            onChange={e => setTemplate(prev => ({ ...prev, section_b_title: e.target.value }))}
            data-testid="section-b-title-input" />
          <div style={{ marginTop: '12px' }}>
            <Label>Subtitle / Note</Label>
            <Input value={template.section_b_subtitle || ''} 
              onChange={e => setTemplate(prev => ({ ...prev, section_b_subtitle: e.target.value }))} />
          </div>
        </div>
      </div>

      {/* Clauses */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <h2 style={{ fontSize: '16px', fontWeight: 600, color: 'var(--navy)' }}>
          General Terms & Conditions ({template.clauses?.length || 0} clauses)
        </h2>
        <Button variant="outline" size="sm" onClick={addClause} data-testid="add-clause-btn"
          style={{ borderColor: 'var(--navy)', color: 'var(--navy)' }}>
          <Plus className="w-4 h-4 mr-1" /> Add Clause
        </Button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '24px' }}>
        {template.clauses?.map((clause, index) => (
          <div key={index} className="form-card" style={{ marginBottom: 0 }}>
            <div 
              style={{ 
                display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 20px',
                cursor: 'pointer', borderBottom: expandedClauses[index] ? '1px solid var(--gray-border)' : 'none'
              }}
              onClick={() => toggleClause(index)}
              data-testid={`clause-header-${index}`}
            >
              <GripVertical className="w-4 h-4" style={{ color: 'var(--text-light)', flexShrink: 0 }} />
              <span style={{ 
                fontFamily: "'DM Mono', monospace", fontSize: '12px', color: 'var(--navy)',
                background: 'var(--gray-light)', padding: '2px 8px', borderRadius: '4px', flexShrink: 0
              }}>
                {index + 1}
              </span>
              <span style={{ fontWeight: 500, flex: 1 }}>{clause.title}</span>
              <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                <button className="icon-btn" onClick={() => moveClause(index, -1)} title="Move up"
                  disabled={index === 0} style={{ opacity: index === 0 ? 0.3 : 1 }}>
                  <ChevronUp className="w-4 h-4" />
                </button>
                <button className="icon-btn" onClick={() => moveClause(index, 1)} title="Move down"
                  disabled={index === template.clauses.length - 1} 
                  style={{ opacity: index === template.clauses.length - 1 ? 0.3 : 1 }}>
                  <ChevronDown className="w-4 h-4" />
                </button>
                <button className="icon-btn" onClick={() => removeClause(index)} title="Remove"
                  style={{ color: 'var(--red)' }} data-testid={`remove-clause-${index}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              {expandedClauses[index] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </div>
            {expandedClauses[index] && (
              <div style={{ padding: '16px 20px' }}>
                <div style={{ marginBottom: '12px' }}>
                  <Label>Clause Title</Label>
                  <Input value={clause.title} 
                    onChange={e => updateClause(index, 'title', e.target.value)} />
                </div>
                <Label>Clause Text</Label>
                <Textarea 
                  value={clause.text} 
                  onChange={e => updateClause(index, 'text', e.target.value)}
                  rows={Math.max(6, (clause.text?.split('\n').length || 1) + 2)}
                  style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '13px', lineHeight: '1.6' }}
                  data-testid={`clause-text-${index}`}
                />
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Annexures */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <h2 style={{ fontSize: '16px', fontWeight: 600, color: 'var(--navy)' }}>
          Annexures ({template.annexures?.length || 0})
        </h2>
        <Button variant="outline" size="sm" onClick={addAnnexure} data-testid="add-annexure-btn"
          style={{ borderColor: 'var(--navy)', color: 'var(--navy)' }}>
          <Plus className="w-4 h-4 mr-1" /> Add Annexure
        </Button>
      </div>

      <div className="form-card" style={{ marginBottom: '24px' }}>
        <div className="form-body-inner" style={{ padding: '16px 20px' }}>
          {template.annexures?.map((ann, index) => (
            <div key={index} style={{ 
              display: 'flex', gap: '12px', alignItems: 'center', 
              padding: '10px 0', borderBottom: index < template.annexures.length - 1 ? '1px solid var(--gray-border)' : 'none'
            }}>
              <Input value={ann.id} onChange={e => updateAnnexure(index, 'id', e.target.value)}
                style={{ width: '60px', fontFamily: "'DM Mono', monospace", textAlign: 'center' }}
                data-testid={`annexure-id-${index}`} />
              <Input value={ann.description} onChange={e => updateAnnexure(index, 'description', e.target.value)}
                style={{ flex: 2 }} placeholder="Description" />
              <Input value={ann.remarks} onChange={e => updateAnnexure(index, 'remarks', e.target.value)}
                style={{ flex: 1 }} placeholder="Remarks" />
              <button className="icon-btn" onClick={() => removeAnnexure(index)} style={{ color: 'var(--red)' }}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom Save */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginBottom: '40px' }}>
        <Button variant="outline" onClick={handleReset}
          style={{ borderColor: 'var(--red)', color: 'var(--red)' }}>
          <RotateCcw className="w-4 h-4 mr-2" /> Reset to Default
        </Button>
        <Button onClick={handleSave} disabled={saving}
          style={{ background: 'var(--navy)', color: '#fff' }}>
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save Template
        </Button>
      </div>
    </div>
  );
};

export default TemplateEditor;
